/** @jsx isolateComponent */

import type { ReactNode } from 'react';
import type { Interpolation } from '@emotion/css';

import { formatTimeSpan } from '@zapier/date';
import { Colors, Icon, Link, Typography } from '@zapier/design-system';
import { isolateComponent, labelStyles } from '@zapier/common-utils';

import UsageBar from './UsageBar';

export type Props = {
  /**
   * Content to render at bottom of section. Typically used to
   * render a CTA to upgrade plan or similar.
   */
  children?: ReactNode;
  /**
   * Whether or not the type of plan is metered
   */
  isPlanMetered: boolean;
  /**
   * URL to where the plan can be managed.
   */
  managePlanHref: string;
  /**
   * Maximum number of free tasks for the usage period.
   */
  numFreeTasksMax: number;
  /**
   * Number of tasks that have been used in the usage period.
   */
  numTasks: number;
  /**
   * Maximum number of tasks that can be used in the usage period.
   */
  numTasksMax: number;
  /**
   * Number of Zaps that are currently on.
   */
  numZaps: number;
  /**
   * Maximum number of Zaps that can be on.
   */
  numZapsMax: number;
  /**
   * String representation of when usage resets.
   */
  resetDate: string;
  /**
   * The title of the section within the sidebar.
   */
  title: string;
};

const Styles = labelStyles(__filename, {
  root: {
    display: 'grid',
    gap: 10,
    whiteSpace: 'nowrap',
  },
  header: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    gap: 20,
    alignItems: 'center',
    ...Typography.base,
    fontSize: 16,
    fontWeight: 600,
    color: Colors.grey700,
  },
  planName: {
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    lineHeight: 1.3,
  },
  content: {
    margin: '0 5px',
  },
  usages: {
    display: 'grid',
    gap: 5,
    marginBottom: 5,
  },
  resetInfo: {
    display: 'grid',
    gap: 10,
    gridTemplateColumns: '1fr auto',
    ...Typography.base,
    fontSize: 10,
    fontWeight: 600,
    lineHeight: '20px',
    color: Colors.grey500,
  },
  managePlanLink: {
    fontWeight: 700,
    textDecoration: 'underline',
    '&:hover, &:focus': {
      color: Colors.blue500,
    },
  },
});

const Usages = (props: Props) => {
  const hasUsedExtraTasks =
    props.isPlanMetered && props.numTasks > props.numFreeTasksMax;
  return (
    <div css={Styles.usages as Interpolation}>
      <UsageBar
        label={props.isPlanMetered ? 'Included Tasks' : 'Tasks'}
        max={props.isPlanMetered ? props.numFreeTasksMax : props.numTasksMax}
        value={
          props.isPlanMetered
            ? Math.min(props.numTasks, props.numFreeTasksMax)
            : props.numTasks
        }
      />
      {hasUsedExtraTasks && (
        <UsageBar
          label="Extra Tasks"
          max={props.numTasksMax - props.numFreeTasksMax}
          value={props.numTasks - props.numFreeTasksMax}
        />
      )}
      <UsageBar label="Zaps" max={props.numZapsMax} value={props.numZaps} />
    </div>
  );
};

/**
 * Renders account usage data within the `UniversalSidebar`.
 */
const UniversalSidebarUsage = (props: Props) => {
  const relativeDate = formatTimeSpan(Date.now(), props.resetDate, false);
  return (
    <section css={Styles.root as Interpolation}>
      <header css={Styles.header as Interpolation}>
        <Icon name="miscWallet" size={30} />
        <h3 css={Styles.planName as Interpolation}>{props.title}</h3>
      </header>
      <div css={Styles.content as Interpolation}>
        <Usages {...props} />
        <p css={Styles.resetInfo as Interpolation}>
          <span>Monthly usage resets in {relativeDate}</span>
          <Link
            color="inherit"
            css={Styles.managePlanLink as Interpolation}
            href={props.managePlanHref}
          >
            Manage Plan
          </Link>
        </p>
      </div>
      {props.children}
    </section>
  );
};

UniversalSidebarUsage.defaultProps = {
  isPlanMetered: false,
  numFreeTasksMax: 0,
  managePlanHref: '/app/settings/billing',
  title: 'Usage',
};

export default UniversalSidebarUsage;
